/**
 * Flow field: Dijkstra from the Core over the whole grid (doc 12).
 *
 * The one clever rule (doc 03): a structure tile is not an obstacle, it is a
 * DELAY — entering it costs `structureHP / REFERENCE_DPS` seconds of chewing on
 * top of the walk. A thin wall is a door to a big enemy, a thick wall is a
 * detour, and a fully sealed Core needs no special case: every route includes
 * eating something, and the field simply routes through the cheapest bite.
 *
 * 4-connected, cost in seconds at reference speed 1 tile/s. Recomputed at most
 * once per tick, on a dirty flag (doc 12: ≤2 ms budget — a 40×40 grid solves in
 * well under a millisecond).
 */

import { REFERENCE_DPS } from './const.ts';
import { SimGrid, T_ROCK } from './grid.ts';
import type { Structure } from './types.ts';

export interface FlowField {
  /** Seconds-to-Core per tile (Infinity = unreachable, i.e. rock). */
  dist: Float64Array;
  /** Index of the next tile toward the Core, or -1 (at the Core / unreachable). */
  next: Int32Array;
}

const DI = [1, -1, 0, 0];
const DJ = [0, 0, 1, -1];

export function computeFlowField(
  grid: SimGrid,
  structures: Structure[],
  core: Structure,
): FlowField {
  const n = grid.size;
  const cells = n * n;
  const dist = new Float64Array(cells).fill(Infinity);
  const next = new Int32Array(cells).fill(-1);

  // Entering a tile costs 1 s walk + its chew delay. Ruins are rubble: passable,
  // no chew (doc 03 — a ruin reserves the FOOTPRINT for building, not the path).
  const enterCost = new Float64Array(cells).fill(1);
  for (const s of structures) {
    if (s.ruin || s.id === core.id) continue;
    const chew = s.hp / REFERENCE_DPS;
    for (let di = 0; di < s.size; di++) {
      for (let dj = 0; dj < s.size; dj++) {
        enterCost[grid.idx(s.i + di, s.j + dj)] = 1 + chew;
      }
    }
  }

  // Binary min-heap of tile indices keyed by dist.
  const heap: number[] = [];
  const push = (t: number): void => {
    heap.push(t);
    let c = heap.length - 1;
    while (c > 0) {
      const p = (c - 1) >> 1;
      if (dist[heap[p]!]! <= dist[heap[c]!]!) break;
      [heap[p], heap[c]] = [heap[c]!, heap[p]!];
      c = p;
    }
  };
  const pop = (): number => {
    const top = heap[0]!;
    const last = heap.pop()!;
    if (heap.length > 0) {
      heap[0] = last;
      let p = 0;
      for (;;) {
        const l = p * 2 + 1;
        const r = l + 1;
        let m = p;
        if (l < heap.length && dist[heap[l]!]! < dist[heap[m]!]!) m = l;
        if (r < heap.length && dist[heap[r]!]! < dist[heap[m]!]!) m = r;
        if (m === p) break;
        [heap[p], heap[m]] = [heap[m]!, heap[p]!];
        p = m;
      }
    }
    return top;
  };

  for (let di = 0; di < core.size; di++) {
    for (let dj = 0; dj < core.size; dj++) {
      const t = grid.idx(core.i + di, core.j + dj);
      dist[t] = 0;
      push(t);
    }
  }

  const done = new Uint8Array(cells);
  while (heap.length > 0) {
    const t = pop();
    if (done[t]) continue;
    done[t] = 1;
    const ti = t % n;
    const tj = (t / n) | 0;
    for (let k = 0; k < 4; k++) {
      const ni = ti + DI[k]!;
      const nj = tj + DJ[k]!;
      if (ni < 0 || nj < 0 || ni >= n || nj >= n) continue;
      const nt = grid.idx(ni, nj);
      if (grid.terrain[nt] === T_ROCK) continue;
      const d = dist[t]! + enterCost[nt]!;
      if (d < dist[nt]!) {
        dist[nt] = d;
        next[nt] = t;
        push(nt);
      }
    }
  }

  return { dist, next };
}
